import { InputManagerSubscriptionTypes } from 'Core/InputManager'
import { ViolationSource } from 'shared/violations/types'
import { Studio } from 'Core/Studio'
import { ITask } from 'Core/TaskAdapter'
import { isEmpty } from 'lodash'
import debounce from 'lodash/debounce'
import { AIDetectorPlugin } from 'Plugins/AIDetector/AIDetector.plugin'
import { PlagiarismPlugin } from 'Plugins/Plagiarism/Plagiarism.plugin'
import { fillDraftData, submitTurnData } from 'Services/tasks.service'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { ConversationsTaskUI } from 'Tasks/ConversationsTask/UI/ConversationsTaskUI'
import {
    AttributeDefinitionType,
    AttributesData,
    ContainerDataType,
    ContainerType,
    ConversationalConfigs,
    ConversationalTurnDataType,
    ConversationalTurnTypes,
    ConversationsDataType,
    EditorType,
    LinearConversationsDataType,
    NotificationPropsType,
    StepTypes,
    SubmissionPlayTurnAPIResponseType,
    TaskConfigType,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { ConversationContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/ConversationContainerStrategy'
import { ActionParam } from 'Core/ActionCore/ActionManager'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'
import { TurnsAttributeGroup } from 'Core/AttributeCore/AttributeGroup/TurnsAttributeGroup'
import { isViewerMode } from 'Utils/task.utils'
import {
    fillProctoringTimeoutExtraInfo,
    fillProctoringTimeoutValues,
} from 'Utils/proctoringSubmit.utils'

export class ConversationsTask
    extends CommonTask<ContainerType.Conversations, ConversationsDataType>
    implements ITask<TaskConfigType<ContainerType.Conversations, ConversationsDataType>>
{
    turnData: ConversationalTurnDataType[] = []
    private isResponseLoading: boolean = false
    private conversationConfigLimit: ConversationalConfigs = {
        turns: 0,
        retryCount: 0,
    }
    private conversationConfigsData: ConversationalConfigs = {
        turns: 0,
        retryCount: 0,
    }

    private userMessage: string = ''

    constructor(studio: Studio) {
        const conversationContainerStrategy = new ConversationContainerStrategy()
        super(studio, conversationContainerStrategy)
        conversationContainerStrategy.setContext({
            task: this,
        })
    }

    public initialize(config: TaskConfigType<ContainerType.Conversations, ConversationsDataType>) {
        this.conversationConfigLimit = {
            turns: config.assignment.task.config.turn_config?.max_allowed_turns ?? -1,
            retryCount:
                config.assignment.task.config.response_generation_config?.max_allowed_times ?? -1,
        }

        const inputEditorBlockConfig = {
            id: 'input-editor-block-1',
            name: 'input-editor-block-1',
            componentProps: {
                onUpdateContent: (message: string) => {
                    this.userMessage = message
                    /*
                     * If message is empty, we need to remove the mandatory violation.
                     * Otherwise, we add the mandatory violation
                     * This is to ensure that the user can submit the task without any mandatory violations
                     * But he won't be able to submit the turn with violations
                     */
                    this.setPluginViolations(!isEmpty(message))
                },
                getValue: () => this.userMessage,
                editorType: EditorType.MarkdownEditor,
                editorConfig: {
                    markdownEditorConfig: {
                        height: '350px',
                    },
                },
            },
        }

        const blocks: ITaskBlock[] = []

        const isEditorStep =
            config.assignment.step?.type === 'MAKER' || config.assignment.step?.type === 'REWORK'

        if (isEditorStep) {
            const inputEditorBlock = this.studio.blockEngine.addBlock(
                'input-editor-block',
                inputEditorBlockConfig
            )
            const cardBlock = this.studio.blockEngine.addBlock('card-block', {
                id: 'card-block-1',
                name: 'card-block-1',
                componentProps: {
                    children: inputEditorBlock.getElement()?.element,
                    title: 'YOUR PROMPT',
                },
            })

            blocks.push(cardBlock)
        }

        this.studio.workbench.enqueueUiUpdate(async workbenchUi => {
            await this.initTurnsData()

            this.ui = createReactElement(ConversationsTaskUI, {
                blocks,
                getInputDetails: () =>
                    config.assignment.task.input_container as ContainerDataType<
                        ContainerType.LinearConversation,
                        LinearConversationsDataType
                    >,
                isLoading: () => this.isResponseLoading,
                getUserMessage: () => this.userMessage,
                updateUserMessage: (message: string) => {
                    this.userMessage = message
                    /*
                     * If message is empty, we need to remove the mandatory violation.
                     * Otherwise, we add the mandatory violation
                     * This is to ensure that the user can submit the task without any mandatory violations
                     * But he won't be able to submit the turn with violations
                     */
                    this.setPluginViolations(!isEmpty(message))
                    this.studio.blockEngine
                        .getBlock('input-editor-block-1')
                        ?.setState('answer', message)
                    this.ui?.updater.forceUpdate()
                },
                getTurnData: () => this.turnData,
                submitTurn: (promptIndex: number) => {
                    this.submitTurnData(promptIndex)
                },
                onClickRegenerateContent: () => {
                    this.onClickRegenerateContent()
                },
                getViolation: this.studio.inputManager.getViolation,
                isEditorStep,
                isRetryUnavailable: () =>
                    this.conversationConfigsData.retryCount ===
                    this.conversationConfigLimit.retryCount,
                isTurnExhausted: () =>
                    this.conversationConfigsData.turns === this.conversationConfigLimit.turns,
                getTurnAttributes: () =>
                    this.attributeManager?.getAttributeGroupsByDefinitionType(
                        AttributeDefinitionType.TURN_EVALUATION
                    ) ?? [],
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)
            this.setPluginViolations(false)

            if (
                config.assignment.task.attributes?.filter(item =>
                    [
                        AttributeDefinitionType.EXTRA_INFO,
                        AttributeDefinitionType.QUALITY_EVALUATION,
                    ].includes(item.type)
                ).length > 0
            ) {
                this.attributeManager?.useAttributeGroup({
                    groupKey: AttributeDefinitionType.QUALITY_EVALUATION,
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    attributes: config.assignment.task.attributes.filter(
                        item => item.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: {
                        useInCollection: true,
                    },
                })

                this.attributeManager?.useAttributeGroup({
                    groupKey: AttributeDefinitionType.EXTRA_INFO,
                    type: AttributeDefinitionType.EXTRA_INFO,
                    attributes: config.assignment.task.attributes.filter(
                        item => item.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: {
                        useInCollection: true,
                    },
                })

                workbenchUi.rightSidebar.addDivider()
            }
        })

        this.studio.inputManager.subscribeByType('markdown', [
            {
                eventType: InputManagerSubscriptionTypes.Update,
                listener: event => {
                    if (!event) return
                    this.ui?.updater.forceUpdate()
                },
            },
        ])
    }

    protected autoSaveTask = debounce(async () => {})

    protected keyBindingActions = (_: KeyboardEvent) => {}

    addMakerActions(): void {
        const makerActions: ActionParam[] = []
        if (!this.isSavedTask) {
            makerActions.push({
                id: 'Submit',
                config: {
                    onClick: this.onClickSubmitHandler,
                },
            })
        }
        const isSkipTaskConfigEnabled =
            this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled
        if (isSkipTaskConfigEnabled) {
            makerActions.push({
                id: 'Skip',
                config: { title: this.isSavedTask ? 'Skip & Exit' : 'Skip' },
            })
        }
        const step = this.studio.taskConfig.assignment.step
        makerActions.push({
            id: 'SubmitAndExit',
            config: { isCollapsed: !this.isSavedTask, onClick: this.onClickSubmitHandler },
        })
        if (step?.type === StepTypes.Maker || step?.type === StepTypes.Rework || !this.isReadOnly) {
            makerActions.push({
                id: 'Exit',
                config: {
                    onClick: async () => {
                        this.studio.exitTask()
                    },
                },
            })
        } else {
            makerActions.push({ id: 'Exit' })
        }
        makerActions.push({
            id: 'SaveForLater',
            config: { title: this.isSavedTask ? 'Save For Later & Exit' : 'Save For Later' },
        })
        this.actionManager?.addActions(makerActions)
    }

    async submitTask(options?: {
        onComplete?: (props?: NotificationPropsType) => void
        feedbackData?: CSATFeedbackData
        useDummyIfEmpty?: boolean
    }) {
        if (options?.useDummyIfEmpty) {
            const stepType = this.initialConfig?.assignment.step?.type
            if (stepType && [StepTypes.Maker, StepTypes.Rework].includes(stepType)) {
                try {
                    const draftOutputContainer = this.taskState?.getDraftOutputContainer()
                    if (!draftOutputContainer) {
                        return super.submitTask(options)
                    }

                    const filled = fillProctoringTimeoutValues(
                        draftOutputContainer,
                        this.initialConfig?.assignment.task.attributes
                    )

                    const turnLevelAttributes =
                        filled.data?.data?.response?.main_info?.value?.turn_annotations?.at(
                            Math.max(this.turnData.length - 1, 0)
                        )

                    const currentState = this.taskState?.getCurrentState()
                    const extraInfo = fillProctoringTimeoutExtraInfo(
                        this.initialConfig?.assignment.task.attributes,
                        filled.data?.data?.response?.extra_info
                    )

                    await submitTurnData({
                        userId: currentState?.user_id ?? '',
                        submissionId: currentState?.submission.id ?? '',
                        timeElapsedInSeconds: Math.ceil(
                            this.studio.taskManager.timer.getElapsedTime() / 1000
                        ),
                        data: {
                            type: ConversationalTurnTypes.ConversationFinalization,
                        },
                        extraInfo: this.transformExtraInfo(extraInfo),
                        supplement: filled.supplement,
                        turnAnnotations: turnLevelAttributes,
                        promptIndex: undefined,
                        pluginData: this.getPluginData(),
                    })

                    options?.onComplete?.({
                        type: 'success',
                        message: 'Time ended. Task was submitted automatically.',
                    })
                    return
                } catch (e) {
                    console.warn(
                        '[proctoring] conversations duration-end play-turn failed; falling back to draft submit',
                        {
                            stepType,
                            submissionId: this.taskState?.getCurrentState()?.submission.id,
                            error: e,
                        }
                    )
                }
            }
            return super.submitTask(options)
        }

        if (
            this.initialConfig?.assignment.step?.type &&
            [StepTypes.Maker, StepTypes.Rework].includes(this.initialConfig.assignment.step.type)
        ) {
            try {
                const draftOutputContainer = this.taskState?.getDraftOutputContainer()
                if (!draftOutputContainer) {
                    console.error('Cannot submit task: task state is not initialized')
                    return
                }

                const turnLevelAttributes = this.taskState
                    ?.getDraftOutputContainer()
                    ?.data.data.response.main_info.value.turn_annotations.at(
                        this.turnData.length - 1
                    )

                const currentState = this.taskState?.getCurrentState()

                const pluginData = this.getPluginData()
                await submitTurnData({
                    userId: currentState?.user_id ?? '',
                    submissionId: currentState?.submission.id ?? '',
                    timeElapsedInSeconds: Math.floor(
                        this.studio.taskManager.timer.getElapsedTime() / 1000
                    ),
                    data: {
                        type: ConversationalTurnTypes.ConversationFinalization,
                    },
                    extraInfo: this.transformExtraInfo(
                        draftOutputContainer.data.data.response.extra_info
                    ),
                    supplement: draftOutputContainer.supplement,
                    turnAnnotations: turnLevelAttributes,
                    promptIndex: undefined,
                    pluginData: pluginData,
                })
                options?.onComplete?.({ type: 'success', message: 'Task submitted successfully' })
            } catch (e) {
                this.studio.workbench.enqueueUiUpdate(ui =>
                    ui.notifier.notification.error({ message: (e as Error).message })
                )
                if ((e as Error).message === 'Task submission cancelled by admin.') {
                    options?.onComplete?.({ type: 'error', message: (e as Error).message })
                }
            }
        } else {
            super.submitTask({
                onComplete: (notifications?: NotificationPropsType) => {
                    this.studio.nextTask(notifications)
                },
                feedbackData: options?.feedbackData,
            })
        }
    }

    private onClickSubmitHandler = async () => {
        if (this.isResponseLoading) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.warning({
                    message: 'Please wait while the response is generating.',
                })
            )
            return
        }

        if (
            this.turnData.length === 0 &&
            (this.initialConfig?.assignment.step?.type === 'MAKER' ||
                this.initialConfig?.assignment.step?.type === 'REWORK')
        ) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({
                    message:
                        'Please include at least one prompt in this turn to proceed with task submission.',
                })
            )
            return
        }

        const canSubmit = await this.validateData()
        if (!canSubmit) {
            return
        }

        // Filter out the errors for mandatory plugin violations except custom validation
        const errors = Object.values(this.studio.inputManager.getViolation().error).filter(
            violation =>
                violation.source !== ViolationSource.Plugin ||
                violation.id.includes('custom_validation') ||
                violation.id.includes('live_feedback')
        ).length

        if (errors > 0) {
            this.modalManager?.showModal('violations-modal')
            this.studio.pluginManager.handleSubmissionViolation()
            this.ui?.updater.forceUpdate()
            return
        }

        this.modalManager?.setModalMetaData('submit-modal', {
            taskMode: this.studio.taskConfig.mode,
        })
        this.modalManager?.showModal('submit-modal')
        this.ui?.updater.forceUpdate()
    }

    private transformExtraInfo(extraInfo: UpdatedAttributesData | undefined): AttributesData {
        if (!extraInfo) {
            return {}
        }

        const transformAttributesData: AttributesData = {}

        for (const [key, data] of Object.entries(extraInfo)) {
            transformAttributesData[key] = {
                data: {
                    value: data.value,
                    value_type: data.value_type,
                },
                value_id: data.value_id ?? crypto.randomUUID(),
            }
        }

        return transformAttributesData
    }

    private async initTurnsData() {
        const outputResponse = (
            this.studio.taskConfig.assignment.task.output_start_state_container?.data
                ?.data as ConversationsDataType
        )?.response

        const originalDraftContainer =
            this.studio.taskConfig.assignment.submission?.draft_output_container

        // Call the API to set the draft container
        if (
            originalDraftContainer?.data === null &&
            outputResponse?.main_info?.value &&
            [StepTypes.Maker, StepTypes.Rework].includes(
                this.studio.taskConfig.assignment.step?.type ?? StepTypes.Maker
            ) &&
            !isViewerMode(this.studio.taskConfig.mode)
        ) {
            const currentState = this.taskState?.getCurrentState()

            await fillDraftData(
                currentState?.user_id ?? '',
                currentState?.submission.id ?? '',
                outputResponse.main_info,
                ConversationalTurnTypes.DraftFill
            )
        }

        const draftContainer = this.taskState?.getDraftOutputContainer()
        if (!draftContainer) {
            return
        }

        const draftContainerData = draftContainer.data.data.response.main_info.value

        if (draftContainerData) {
            for (let i = 0; i < draftContainerData.turns.length; i++) {
                const turn = draftContainerData.turns[i]

                this.turnData.push({
                    prompt: turn.prompt,
                    modelResponse: turn.external_model_response,
                    promptRounds: turn.prompt_rounds.at(-1)?.responses_list ?? [],
                })

                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.TURN_EVALUATION,
                    groupKey: `turn-evaluation-${i}`,
                    attributes: this.initialConfig.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.TURN_EVALUATION
                    ),
                    config: {
                        useInCollection: false,
                        props: {
                            header: '',
                        },
                    },
                })
                if (i < draftContainerData.turns.length - 1) {
                    ;(
                        this.attributeManager?.getAttributeGroup(
                            `turn-evaluation-${i}`
                        ) as TurnsAttributeGroup
                    )?.disable()
                }
            }

            this.conversationConfigsData.turns = this.turnData.length
        }
    }

    /*
    We need to disable all plugin violations when the task is initialized. 
    We only need to check for plugin violations before the user submits the prompt for next turn.
    */
    private setPluginViolations(enable: boolean) {
        const plugins = this.studio.pluginManager.getInstalledPlugins()
        plugins.forEach(plugin => {
            if (['AI Detection', 'Plagiarism'].includes(plugin.id)) {
                if (enable) {
                    ;(plugin as PlagiarismPlugin | AIDetectorPlugin).addMandatoryViolation()
                } else {
                    ;(plugin as PlagiarismPlugin | AIDetectorPlugin).removeMandatoryViolation()
                }
            }
        })

        this.ui?.updater.forceUpdate()
    }

    private onClickRegenerateContent = async () => {
        const lastTurnData = this.turnData.at(-1)

        lastTurnData!.modelResponse = ''
        this.isResponseLoading = true
        this.ui?.updater.forceUpdate()

        const currentState = this.taskState?.getCurrentState()
        const draftOutputContainer = this.taskState?.getDraftOutputContainer()

        if (!draftOutputContainer) {
            console.error('Cannot submit turn data: task state is not initialized')
            return
        }

        ;(
            this.attributeManager?.getAttributeGroup(
                `turn-evaluation-${this.turnData.length - 1}`
            ) as TurnsAttributeGroup
        ).removeAllAttributes()

        try {
            const response = (await submitTurnData({
                userId: currentState?.user_id ?? '',
                submissionId: currentState?.submission.id ?? '',
                timeElapsedInSeconds: Math.floor(
                    this.studio.taskManager.timer.getElapsedTime() / 1000
                ),
                data: {
                    type: ConversationalTurnTypes.PromptRegeneration,
                    data: { prompt: lastTurnData?.prompt },
                },
                extraInfo: this.transformExtraInfo(
                    draftOutputContainer.data.data.response.extra_info
                ),
            })) as SubmissionPlayTurnAPIResponseType

            lastTurnData!.modelResponse =
                response?.submission.output_container.data.data.response.main_info.value.turns.at(
                    -1
                )?.external_model_response ?? ''
        } catch {
            lastTurnData!.modelResponse =
                'Something went wrong while generating the response. Please try again.'
        } finally {
            this.conversationConfigsData.retryCount = this.conversationConfigsData.retryCount + 1
            this.isResponseLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    private clearAttributeGroupsFromIndex = (index: number) => {
        for (let i = index; i < this.turnData.length; i++) {
            const groupKey = `turn-evaluation-${i}`
            this.attributeManager?.removeAttributeGroup(groupKey)
        }
    }

    private submitTurnData = async (promptIndex: number) => {
        const isValidData = await this.attributeManager?.validateGroupsByDefinitionType(
            AttributeDefinitionType.TURN_EVALUATION
        )

        if (!isValidData && promptIndex === -1) {
            return
        }

        this.isResponseLoading = true
        this.conversationConfigsData.retryCount = 0

        const currentState = this.taskState?.getCurrentState()

        let type: string
        let turnLevelAttributes = this.taskState
            ?.getDraftOutputContainer()
            ?.data.data.response.main_info.value.turn_annotations.at(this.turnData.length - 1)
        if (promptIndex !== -1) {
            type = ConversationalTurnTypes.PromptEdit
        } else if (this.turnData.length === 0) {
            type = ConversationalTurnTypes.Conversation
            turnLevelAttributes = undefined
        } else {
            type = ConversationalTurnTypes.ConversationNewTurn
        }

        if (promptIndex !== -1) {
            this.turnData[promptIndex] = {
                prompt: this.userMessage,
                modelResponse: '',
                promptRounds: [],
            }

            this.clearAttributeGroupsFromIndex(promptIndex)
            this.turnData = this.turnData.slice(0, promptIndex + 1)
            ;(
                this.attributeManager?.getAttributeGroup(
                    `turn-evaluation-${this.turnData.length - 1}`
                ) as TurnsAttributeGroup
            )?.enable()
        } else {
            this.turnData.push({
                prompt: this.userMessage,
                modelResponse: '',
                promptRounds: [],
            })
            ;(
                this.attributeManager?.getAttributeGroup(
                    `turn-evaluation-${this.turnData.length - 2}`
                ) as TurnsAttributeGroup
            )?.disable()
        }

        const payload = {
            type,
            data: { prompt: this.userMessage },
        }

        this.userMessage = ''
        this.ui?.updater.forceUpdate()
        // TODO - Conversations - Check on better alternatives for setState
        this.studio.blockEngine.getBlock('input-editor-block-1')?.setState('answer', '')

        try {
            const response = (await submitTurnData({
                userId: currentState?.user_id ?? '',
                submissionId: currentState?.submission.id ?? '',
                timeElapsedInSeconds: Math.floor(
                    this.studio.taskManager.timer.getElapsedTime() / 1000
                ),
                data: payload,
                extraInfo: undefined,
                supplement: undefined,
                turnAnnotations: turnLevelAttributes,
                promptIndex: promptIndex !== -1 ? promptIndex : undefined,
                pluginData: undefined,
            })) as SubmissionPlayTurnAPIResponseType

            const turnData =
                response?.submission.output_container.data.data.response.main_info.value
            const draftContainer = this.taskState?.getDraftOutputContainer()

            if (!turnData || !draftContainer) {
                return
            }

            // Update the draft container with the updated turn data
            const draftContainerData = draftContainer?.data.data.response.main_info.value
            draftContainerData.turns = turnData.turns
            draftContainerData.turn_annotations = turnData.turn_annotations ?? []

            this.taskState?.updateDraftOutputContainer(draftContainer)

            this.attributeManager?.useAttributeGroup({
                type: AttributeDefinitionType.TURN_EVALUATION,
                groupKey: `turn-evaluation-${this.turnData.length - 1}`,
                attributes: this.initialConfig.assignment.task.attributes.filter(
                    attr => attr.type === AttributeDefinitionType.TURN_EVALUATION
                ),
                config: {
                    useInCollection: false,
                    props: {
                        header: '',
                    },
                },
            })

            this.turnData.at(-1)!.modelResponse =
                turnData.turns.at(-1)?.external_model_response ?? ''
            this.turnData.at(-1)!.promptRounds =
                turnData.turns.at(-1)?.prompt_rounds[0].responses_list ?? []

            this.conversationConfigsData.turns = this.turnData.length
            this.attributeManager?.validateGroupsByDefinitionType(
                AttributeDefinitionType.TURN_EVALUATION
            )
        } catch {
            this.turnData[this.turnData.length - 1].modelResponse =
                'Something went wrong while generating the response. Please try again.'
        } finally {
            this.isResponseLoading = false
            this.ui?.updater.forceUpdate()
        }
    }
}
